import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useI18n } from "@/i18n";

const KEY = "xorzeni.consent.v2";

function applyConsent(state) {
  if (typeof window === "undefined" || typeof window.gtag !== "function") return;
  window.gtag("consent", "update", state);
}

export default function CookieBanner() {
  const { t, lang } = useI18n();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const stored = (() => {
      try { return localStorage.getItem(KEY); } catch (e) { return null; }
    })();
    if (!stored) {
      setVisible(true);
    } else {
      try { applyConsent(JSON.parse(stored)); } catch (e) {}
    }
    const reopen = () => setVisible(true);
    window.addEventListener("xorzeni:reopen-consent", reopen);
    return () => window.removeEventListener("xorzeni:reopen-consent", reopen);
  }, []);

  const save = (state) => {
    try { localStorage.setItem(KEY, JSON.stringify(state)); } catch (e) {}
    applyConsent(state);
    setVisible(false);
  };

  const acceptAll = () => save({
    ad_storage: "granted",
    ad_user_data: "granted",
    ad_personalization: "granted",
    analytics_storage: "granted",
    functionality_storage: "granted",
    security_storage: "granted",
  });
  const rejectAll = () => save({
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    analytics_storage: "denied",
    functionality_storage: "granted",
    security_storage: "granted",
  });
  const essentialOnly = () => save({
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    analytics_storage: "denied",
    functionality_storage: "granted",
    security_storage: "granted",
  });

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-0 inset-x-0 z-50 border-t-2 border-[var(--brand-terracotta)] bg-white shadow-[0_-8px_24px_rgba(11,42,74,0.12)]"
      role="dialog"
      aria-live="polite"
      aria-labelledby="cookie-banner-title"
      data-testid="cookie-banner"
    >
      <div className="max-w-[1240px] mx-auto px-6 lg:px-10 py-5 flex flex-col lg:flex-row lg:items-center gap-5">
        <div className="flex-1">
          <h3 id="cookie-banner-title" className="text-[var(--brand-navy)] font-serif-brand text-lg font-semibold mb-1">
            {t("cookieBanner.title")}
          </h3>
          <p className="text-sm text-[var(--brand-ink)]/80 leading-relaxed">
            {t("cookieBanner.text")}{" "}
            <Link to={`/${lang}/cookie-policy`} className="underline text-[var(--brand-navy)]" data-testid="cookie-banner-learn-more">
              {t("cookieBanner.learnMore")}
            </Link>
          </p>
        </div>
        <div className="flex flex-wrap gap-3 shrink-0">
          <button
            type="button"
            onClick={rejectAll}
            className="px-4 py-2 text-sm border border-[var(--brand-navy)] text-[var(--brand-navy)] hover:bg-[var(--brand-navy)]/5"
            data-testid="cookie-banner-reject"
          >
            {t("cookieBanner.reject")}
          </button>
          <button
            type="button"
            onClick={essentialOnly}
            className="px-4 py-2 text-sm border border-[var(--brand-mineral)] text-[var(--brand-ink)] hover:bg-[var(--brand-line)]"
            data-testid="cookie-banner-essential"
          >
            {t("cookieBanner.settings")}
          </button>
          <button
            type="button"
            onClick={acceptAll}
            className="px-4 py-2 text-sm bg-[var(--brand-terracotta)] hover:bg-[var(--brand-terracotta-2)] text-white"
            data-testid="cookie-banner-accept"
          >
            {t("cookieBanner.accept")}
          </button>
        </div>
      </div>
    </div>
  );
}
